import React, { FC, useState } from 'react'
import { Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { FormatInsertRuleForm } from '@/apps/latest/components/molecules/FormatInsertRuleForm'
import { FormatInsertRuleTable } from '@/apps/latest/components/portal/FormatInsertRuleTable'
import {
  useFormatInsertRulesQuery,
  useCreateFormatInsertRuleMutation,
  useUpdateFormatInsertRuleMutation,
  useToggleFormatInsertRuleMutation,
  useDeleteFormatInsertRuleMutation,
} from '@/features/internal/format-insert-rules/hooks/useFormatInsertRulesQuery'
import { useToast } from '@/shared/contexts/ToastContext'
import { FormatInsertRule } from '@/features/internal/format-insert-rules/types/formatInsertRule'
import {
  Banner,
  DialogShell,
  NotionButton,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const FormatInsertSettingsPage: FC = () => {
  const { showToast } = useToast()
  const { data, isLoading: loading, error } = useFormatInsertRulesQuery()
  const rules = data ?? []
  const createMutation = useCreateFormatInsertRuleMutation()
  const updateMutation = useUpdateFormatInsertRuleMutation()
  const toggleMutation = useToggleFormatInsertRuleMutation()
  const deleteMutation = useDeleteFormatInsertRuleMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<FormatInsertRule | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreate = () => {
    setSelectedRule(null)
    setDialogMode('create')
  }

  const handleEdit = (rule: FormatInsertRule) => {
    setSelectedRule(rule)
    setDialogMode('edit')
  }

  const handleDelete = (id: string) => {
    const rule = rules.find((r) => r.id === id)
    if (rule) {
      setSelectedRule(rule)
      setDialogMode('delete')
    }
  }

  const handleSubmitRule = async (ruleData: FormatInsertRule) => {
    try {
      setIsSubmitting(true)
      if (dialogMode === 'create') {
        await createMutation.mutateAsync(ruleData)
      } else if (dialogMode === 'edit' && selectedRule?.id) {
        await updateMutation.mutateAsync({
          id: selectedRule.id,
          rule: ruleData,
        })
      }
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedRule?.id) return
    try {
      setIsSubmitting(true)
      await deleteMutation.mutateAsync(selectedRule.id)
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (id: string) => {
    try {
      const result = await toggleMutation.mutateAsync(id)
      showToast(result.message, 'success')
    } catch (_err) {
      showToast('ルールの状態変更に失敗しました', 'error')
    }
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
    }
  }

  if (error) {
    return (
      <PageScaffold title="フォーマット自動挿入設定">
        <Banner severity="danger">{error.message}</Banner>
      </PageScaffold>
    )
  }

  return (
    <PageScaffold
      title="フォーマット自動挿入設定"
      meta={rules.length > 0 ? `全 ${rules.length} 件` : undefined}
      actions={
        <NotionButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          新規ルール
        </NotionButton>
      }
    >
      <FormatInsertRuleTable
        rules={rules}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      <DialogShell
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        title={dialogMode === 'create' ? 'ルール作成' : 'ルール編集'}
        maxWidth="md"
        disabled={isSubmitting}
      >
        <FormatInsertRuleForm
          rule={selectedRule || undefined}
          onSubmit={handleSubmitRule}
          onCancel={handleCloseDialog}
          isLoading={isSubmitting}
        />
      </DialogShell>

      <DialogShell
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        title="ルール削除確認"
        maxWidth="xs"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton
              variant="secondary"
              onClick={handleCloseDialog}
              disabled={isSubmitting}
            >
              キャンセル
            </NotionButton>
            <NotionButton
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? '削除中…' : '削除'}
            </NotionButton>
          </>
        }
      >
        <Typography sx={PORTAL_TEXT.rowLabel}>
          「{selectedRule?.name}」を削除しますか？
        </Typography>
        <Typography sx={{ ...PORTAL_TEXT.cellMeta, mt: 0.5 }}>
          この操作は取り消すことができません。
        </Typography>
      </DialogShell>
    </PageScaffold>
  )
}

export default FormatInsertSettingsPage
